import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Slide,
  Divider,
} from "@mui/material";
import { Email, Lock, CheckCircle, Send, Verified } from "@mui/icons-material";
import { requestOTP, verifyOTP } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Custom Step Icons that properly handle props
const EmailStepIcon = (props) => {
  const { active, completed, error, ...other } = props;
  return (
    <div {...other}>
      <Email color={active ? "primary" : completed ? "success" : "disabled"} />
    </div>
  );
};

const LockStepIcon = (props) => {
  const { active, completed, error, ...other } = props;
  return (
    <div {...other}>
      <Lock color={active ? "primary" : completed ? "success" : "disabled"} />
    </div>
  );
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleRequestOTP = async () => {
    if (!email) {
      setError("Please enter your email");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await requestOTP(email);
      if (response.success) {
        setOrganisations(response.organisations || []);
        if (response.organisations.length === 1) {
          setSelectedOrg(response.organisations[0]);
        }
        setActiveStep(1);
        setSuccess("OTP sent successfully! Check your email.");
      } else {
        setError(response.error || "Failed to send OTP");
      }
    } catch (error) {
      setError(error.response?.data?.error || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await verifyOTP(email, otp, selectedOrg?.id);

      if (response.success) {
        login(response.token, response.user);
        navigate("/dashboard");
      } else {
        setError(response.error || "Invalid OTP");
        if (response.organisations && response.organisations.length > 1) {
          setOrganisations(response.organisations);
        }
      }
    } catch (error) {
      setError(error.response?.data?.error || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    handleRequestOTP();
  };

  const handleOrganisationSelect = (org) => {
    setSelectedOrg(org);
    setError("");
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    if (value.length === 6 && organisations.length === 1) {
      setTimeout(() => {
        handleVerifyOTP();
      }, 300);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        p: 2,
      }}
    >
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Card
          sx={{ maxWidth: 520, width: "100%", borderRadius: 4, boxShadow: 8 }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
                <Box
                  component="img"
                  src="/unftp-logo-new.png"
                  alt="UNFTP Logo"
                  sx={{ height: 70, width: "auto", objectFit: "contain" }}
                />
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: "#2d3748" }}>
                UNFTP Interface
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            <Stepper activeStep={activeStep} orientation="vertical">
              <Step>
                <StepLabel StepIconComponent={EmailStepIcon}>
                  <Typography fontWeight={500}>Enter Email</Typography>
                </StepLabel>
                <StepContent>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading || activeStep > 0}
                    sx={{ mb: 2 }}
                    placeholder="you@example.com"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleRequestOTP();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleRequestOTP}
                    disabled={loading || !email || activeStep > 0}
                    fullWidth
                    startIcon={<Send />}
                    sx={{ borderRadius: 2, py: 1.5 }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Send OTP"}
                  </Button>
                </StepContent>
              </Step>

              <Step>
                <StepLabel StepIconComponent={LockStepIcon}>
                  <Typography fontWeight={500}>
                    {organisations.length > 1
                      ? "Select Organisation & Verify"
                      : "Verify OTP"}
                  </Typography>
                </StepLabel>
                <StepContent>
                  {organisations.length > 1 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        You have multiple organisations. Please select one:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {organisations.map((org) => (
                          <Button
                            key={org.id}
                            variant={
                              selectedOrg?.id === org.id
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() => handleOrganisationSelect(org)}
                            fullWidth
                            sx={{
                              justifyContent: "flex-start",
                              borderRadius: 2,
                              py: 1.5,
                            }}
                          >
                            <Box sx={{ textAlign: "left" }}>
                              <Typography variant="body1" fontWeight={500}>
                                {org.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {org.country}
                              </Typography>
                            </Box>
                            {selectedOrg?.id === org.id && (
                              <CheckCircle
                                sx={{ ml: "auto", color: "success.main" }}
                              />
                            )}
                          </Button>
                        ))}
                      </Box>
                      <Divider sx={{ my: 2 }} />
                    </Box>
                  )}

                  {organisations.length === 1 && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2">
                        Auto-login enabled for{" "}
                        <strong>{organisations[0].name}</strong>
                      </Typography>
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Enter OTP"
                    value={otp}
                    onChange={handleOtpChange}
                    placeholder="6-digit code"
                    disabled={loading}
                    sx={{ mb: 2 }}
                    inputProps={{ maxLength: 6 }}
                    helperText={
                      organisations.length === 1
                        ? "OTP will auto-verify when complete"
                        : "Enter the 6-digit code sent to your email"
                    }
                  />

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleVerifyOTP}
                      disabled={loading || !otp || otp.length !== 6}
                      startIcon={<Verified />}
                      sx={{ flex: 1, borderRadius: 2, py: 1.5 }}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Verify & Login"
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleResendOTP}
                      disabled={loading}
                      sx={{ borderRadius: 2, px: 3 }}
                    >
                      Resend
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </CardContent>
        </Card>
      </Slide>
    </Box>
  );
};

export default Login;
